import React, { useContext, useEffect, useMemo, useState } from "react";
import RecentOrders from "../components/RecentOrders";
import Selector from "../components/SelectComponent";
import {
  formatDateTime,
  getDateRange,
  useDecodedTokenResult,
} from "../lib/utils";
import {
  useGetCustomersCountsQuery,
  useGetDateWiseSalesCountsQuery,
  useGetOrderAndCustomerCountsQuery,
  useGetOrderStatusCountsQuery,
  usePendingPaymentOrdersQuery,
} from "../api/api";
import { SalesChart } from "../components/SalesChart";
import { NotificationContext } from "../components/ProtectedRoute";
import InfoTooltip from "../components/common/InfoTooltip";
import { USER_ROLE } from "../lib/constants";

type RangeType = "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth";

interface RangeState {
  order: RangeType;
  customer: RangeType;
  allOrders: RangeType;
}

interface Errors {
  orders: boolean;
  customers: boolean;
  sales: boolean;
}

const Dashboard: React.FC = () => {
  const { role } = useDecodedTokenResult();
  const isMobile = window.innerWidth < 768;
  const [ranges, setRanges] = useState<RangeState>({
    order: "thisWeek",
    customer: "thisWeek",
    allOrders: "thisWeek",
  });

  const [dataErrors, setDataErrors] = useState<Errors>({
    orders: true,
    customers: true,
    sales: true,
  });

  const dateRanges = useMemo(
    () => ({
      order: getDateRange(ranges.order),
      customer: getDateRange(ranges.customer),
      allOrders: getDateRange(ranges.allOrders),
    }),
    [ranges]
  );

  function handleRangeChange(type: keyof RangeState, range: RangeType) {
    setRanges((prev) => ({ ...prev, [type]: range }));
  }

  const {
    data: ordersData,
    isFetching: isFetchingOrders,
    isSuccess: ordersSuccess,
    refetch: refetchOrderAndCustomerCounts,
  } = useGetOrderAndCustomerCountsQuery(
    {
      startDate: dateRanges.order.startDate,
      endDate: dateRanges.order.endDate,
    },
    {
      skip: !dateRanges.order.startDate || !dateRanges.order.endDate,
      refetchOnFocus: false,
    }
  );

  const {
    data: allOrdersData,
    isFetching: isFetchingAllOrders,
    isSuccess: allOrdersSuccess,
    refetch: refetchOrderStatusCounts,
  } = useGetOrderStatusCountsQuery(
    {
      startDate: dateRanges.allOrders.startDate,
      endDate: dateRanges.allOrders.endDate,
    },
    {
      skip: !dateRanges.allOrders.startDate || !dateRanges.allOrders.endDate,
      refetchOnFocus: false,
    }
  );

  const {
    data: customersData,
    isFetching: isFetchingCustomers,
    isSuccess: customersSuccess,
    refetch: refetchCustomersCounts,
  } = useGetCustomersCountsQuery(
    {
      startDate: dateRanges.customer.startDate,
      endDate: dateRanges.customer.endDate,
    },
    {
      skip: !dateRanges.customer.startDate || !dateRanges.customer.endDate,
      refetchOnFocus: false,
    }
  );

  const [currentDate, setCurrentDate] = useState(
    formatDateTime(new Date(), "YYYY-MM-DDTHH:mm:ssZ")
  );

  const memoizedCurrentDate = useMemo(() => {
    return formatDateTime(new Date(), "YYYY-MM-DDTHH:mm:ssZ");
  }, []);

  const {
    data: salesData,
    isFetching: isFetchingSales,
    isSuccess: salesDataSuccess,
    refetch: refetchDateWiseSalesCounts,
  } = useGetDateWiseSalesCountsQuery(
    {
      salesDate: memoizedCurrentDate,
    },
    {
      skip: !dateRanges.customer.startDate || !dateRanges.customer.endDate,
      refetchOnFocus: false,
    }
  );

  const {
    data: ordersWithPendingPaymentData,
    isSuccess: ordersWithPendingPaymentSuccess,
    isFetching: ordersWithPendingPaymentFetching,
    refetch: refetchPendingPaymentOrders,
  } = usePendingPaymentOrdersQuery(undefined, {
    skip: role !== USER_ROLE.VENDOR,
  });

  useEffect(() => {
    setDataErrors({
      orders: !ordersSuccess || !ordersData,
      customers: !customersSuccess || !customersData,
      sales: !salesDataSuccess || !salesData,
    });
  }, [
    ordersSuccess,
    ordersData,
    allOrdersSuccess,
    allOrdersData,
    customersSuccess,
    customersData,
    salesDataSuccess,
    salesData,
  ]);

  const notifications = useContext(NotificationContext);

  useEffect(() => {
    refetchDateWiseSalesCounts();
    refetchCustomersCounts();
    refetchOrderStatusCounts();
    if (role === USER_ROLE.VENDOR) {
      refetchPendingPaymentOrders();
    } else {
      refetchOrderAndCustomerCounts();
    }
  }, [notifications]);

  return (
    <>
      <div
        className={`p-4 px-6 bg-grayBackgroundColor w-full md:overflow-hidden overflow-y-auto ${
          isMobile ? "mb-20" : ""
        }`}
      >
        <div className="grid 2xl:grid-cols-4 lg:grid-cols-2 min-[589px]:grid-cols-2 grid-cols-1 gap-3 mb-4">
          <div className="bg-white p-2 rounded shadow">
            <div className="flex justify-between items-center">
              <div className="flex items-center mb-1">
                <div className="flex items-center justify-center w-7 h-7 bg-customGreenLight rounded">
                  <img
                    src="/Images/DashboardImages/Bag.png"
                    className="text-customGreenColor w-4 h-4"
                    alt="Bag"
                  />
                </div>
                <h2 className="text-lg font-helveticaMedium text-customGreenColor ml-1">
                  Today's Sale
                </h2>
                <InfoTooltip content="Shows the total sales amount for today" />
              </div>
            </div>
            <div className="flex justify-end items-end mt-8">
              <div>
                <p className="xxxl:text-3xl text-2xl font-semibold text-customGreenColor">
                  {isFetchingSales ? (
                    <div className="loader h-6 mt-4" />
                  ) : salesDataSuccess === false ? (
                    "- - -"
                  ) : (
                    `$${salesData?.data?.totalSales}`
                  )}
                </p>
              </div>
            </div>
          </div>
          {/* Orders Card */}
          <div className="bg-white p-2 rounded shadow">
            <div className="flex justify-between items-center">
              <div className="flex items-center mb-1">
                <div className="flex items-center justify-center w-7 h-7 bg-customGreenLight rounded">
                  <img
                    src="/Images/DashboardImages/fi_shopping-cart.png"
                    className="text-customGreenColor w-4 h-4"
                    alt="fi_shopping-cart"
                  />
                </div>
                <h2 className="text-lg font-helveticaMedium text-black ml-1">
                  Order
                </h2>
                <InfoTooltip content="Shows total orders and customers for the selected time period" />
              </div>
              <div className="text-xs text-neutral-300">
                <Selector
                  selectedRange={ranges.order}
                  onRangeChange={(range) => handleRangeChange("order", range)}
                />
              </div>
            </div>
            <div className="flex justify-between mt-5">
              <div className="min-h-[44px] flex flex-col items-center justify-between">
                <p className="text-xs font-helveticaMedium text-neutral-400">
                  Customers
                </p>
                <p className="text-lg font-helveticaMedium">
                  {isFetchingOrders ? (
                    <div className="loader" />
                  ) : dataErrors.orders ? (
                    "- - -"
                  ) : (
                    ordersData?.data?.customersCount
                  )}
                </p>
              </div>
              <div className="min-h-[44px] flex flex-col items-center justify-between">
                <p className="text-xs font-helveticaMedium text-neutral-400">
                  Orders
                </p>
                <p className="text-lg font-helveticaMedium">
                  {isFetchingOrders ? (
                    <div className="loader" />
                  ) : ordersSuccess === false ? (
                    "- - -"
                  ) : (
                    ordersData?.data?.allOrdersCount
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-2 rounded shadow">
            <div className="flex flex-wrap justify-between items-center">
              <div className="flex items-center mb-1">
                <div className="flex items-center justify-center w-7 h-7 bg-customGreenLight rounded">
                  <img
                    src="/Images/DashboardImages/fi_shopping-cart.png"
                    className="text-customGreenColor w-4 h-4"
                    alt="bag2"
                  />
                </div>

                <h2 className="text-lg font-helveticaMedium text-black ml-1">
                  All Orders
                </h2>
                <InfoTooltip content="Overview of all orders including pending and completed orders" />
              </div>
              <div className="text-xs text-neutral-300">
                <Selector
                  selectedRange={ranges.allOrders}
                  onRangeChange={(range) =>
                    handleRangeChange("allOrders", range)
                  }
                />
              </div>
            </div>
            <div className="flex justify-between mt-5">
              <div className="min-h-[44px] flex flex-col items-center justify-between">
                <p className="text-xs font-helveticaMedium text-neutral-400">
                  All Orders
                </p>
                <p className="text-lg font-helveticaMedium">
                  {isFetchingAllOrders ? (
                    <div className="loader" />
                  ) : allOrdersSuccess === false ? (
                    "- - -"
                  ) : (
                    allOrdersData?.data?.allOrdersCount
                  )}
                </p>
              </div>
              <div className="min-h-[44px] flex flex-col items-center justify-between">
                <p className="text-xs font-helveticaMedium text-neutral-400">
                  Pending
                </p>
                <p className="text-lg font-helveticaMedium">
                  {isFetchingAllOrders ? (
                    <div className="loader" />
                  ) : allOrdersSuccess === false ? (
                    "- - -"
                  ) : (
                    allOrdersData?.data?.activatedOrdersCount
                  )}
                </p>
              </div>
              <div className="min-h-[44px] flex flex-col items-center justify-between">
                <p className="text-xs font-helveticaMedium text-neutral-400">
                  Completed
                </p>
                <p className="text-lg font-helveticaMedium">
                  {isFetchingAllOrders ? (
                    <div className="loader" />
                  ) : allOrdersSuccess === false ? (
                    "- - -"
                  ) : (
                    allOrdersData?.data?.deliveredOrdersCount
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-2 rounded shadow">
            <div className="flex justify-between items-center">
              <div className="flex items-center mb-1">
                <div className="flex items-center justify-center w-7 h-7 bg-customGreenLight rounded">
                  <img
                    src="/Images/DashboardImages/User.png"
                    className="text-customGreenColor w-4 h-4"
                    alt="User"
                  />
                </div>
                <h2 className="text-lg font-helveticaMedium text-black ml-1">
                  {role === USER_ROLE.VENDOR ? "Payout" : "Customers"}
                </h2>
                {role === USER_ROLE.VENDOR ? (
                  <InfoTooltip
                    placement="left"
                    content="Displays the total receivable amount for orders with the status 'Pending Payment'"
                  />
                ) : (
                  <InfoTooltip
                    placement="left"
                    content="Displays total and active customers for the selected period"
                  />
                )}
              </div>
              {role === USER_ROLE.ADMIN && (
                <div className="text-xs text-neutral-300">
                  <Selector
                    selectedRange={ranges.customer}
                    onRangeChange={(range) =>
                      handleRangeChange("customer", range)
                    }
                  />
                </div>
              )}
            </div>
            {role === USER_ROLE.VENDOR ? (
              <div className="flex justify-end items-end mt-8">
                <div className="min-h-[44px] flex flex-col items-center justify-between">
                  <p className="xxxl:text-3xl text-2xl font-semibold text-customGreenColor">
                    {ordersWithPendingPaymentFetching ? (
                      <div className="loader h-6 mt-4" />
                    ) : ordersWithPendingPaymentSuccess === false ? (
                      "- - -"
                    ) : (
                      `$${ordersWithPendingPaymentData?.data}`
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex justify-between mt-5">
                <div className="min-h-[44px] flex flex-col items-center justify-between">
                  <p className="text-xs font-helveticaMedium text-neutral-400">
                    Customers
                  </p>
                  <p className="text-lg font-helveticaMedium">
                    {isFetchingCustomers ? (
                      <div className="loader" />
                    ) : customersSuccess === false ? (
                      "- - -"
                    ) : (
                      customersData?.data?.totalCustomersCount
                    )}
                  </p>
                </div>
                <div className="min-h-[44px] flex flex-col items-center justify-between">
                  <p className="text-xs font-helveticaMedium text-neutral-400">
                    Active
                  </p>
                  <p className="text-lg font-helveticaMedium">
                    {isFetchingCustomers ? (
                      <div className="loader" />
                    ) : customersSuccess === false ? (
                      "- - -"
                    ) : (
                      customersData?.data?.activeCustomersCount
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="max-h-min bg-transparent w-full">
          <div className="grid md:grid-cols-7 grid-cols-1 md:overflow-hidden gap-x-4 pb-2 h-full">
            <div className="md:col-span-5 h-full w-auto flex flex-col hidden md:flex">
              <SalesChart />
            </div>
            <div className="md:col-span-2 rounded-lg flex flex-col h-full md:mt-0 mt-4 mb-4">
              <RecentOrders />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
